import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/sections/CTASection";
import { getBlogPosts, getBlogCategories } from "../lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    getBlogPosts().then(setPosts).catch(() => {});
    getBlogCategories().then((d) => setCats(d.categories || [])).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => active === "All" ? posts : posts.filter((p) => p.category === active),
    [active, posts]
  );

  const featured = posts[0];

  return (
    <>
      <SEO
        title="Indiana Accident Blog | Insights & Guides for Accident Victims"
        description="Expert articles, legal guides, and Indiana-specific advice for accident victims. From statutes of limitations to settlement timelines."
      />
      <section className="pt-12 md:pt-20 pb-12 px-6 bg-bone">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-3">The Blog</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance max-w-3xl">
            Indiana accident insights & legal guides.
          </h1>
          <p className="text-lg text-charcoal-soft max-w-2xl">
            Plain-English answers to the questions Indiana accident victims actually ask. Updated regularly.
          </p>
        </div>
      </section>

      {featured && (
        <section className="px-6 pb-12">
          <div className="max-w-7xl mx-auto">
            <Link
              to={`/blog/${featured.slug}`}
              className="block group bg-forest text-bone rounded-3xl overflow-hidden p-8 md:p-12 hover:-translate-y-1 transition-all duration-300"
              data-testid="blog-featured-card"
            >
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-3">Featured</div>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-bone tracking-tight mb-4 leading-tight max-w-3xl">
                {featured.title}
              </h2>
              <p className="text-base md:text-lg text-bone/70 max-w-2xl leading-relaxed mb-6">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-bone/60">
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.read_time} min read</span>
                <span>•</span>
                <span>{featured.category}</span>
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="px-6 pb-12">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 mb-8">
          {["All", ...cats].map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                active === c
                  ? "bg-forest text-bone border-forest"
                  : "bg-white text-charcoal border-black/10 hover:border-forest"
              }`}
              data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="card-elevated p-6 md:p-7 group flex flex-col"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="text-[10px] font-bold tracking-wider uppercase text-terracotta mb-3">{p.category}</div>
              <h3 className="font-display font-bold text-xl md:text-2xl text-forest mb-3 leading-tight group-hover:text-terracotta transition-colors">
                {p.title}
              </h3>
              <p className="text-sm md:text-base text-charcoal-soft leading-relaxed flex-1 mb-4">{p.excerpt}</p>
              <div className="flex items-center gap-2 text-xs text-charcoal-soft">
                <Clock className="w-3.5 h-3.5" />
                {p.read_time} min read
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
